import React from 'react';
import { GENDER_FILL, GENDER_VALUE_TEXT, TRACK } from '../theme';
import { barLength, fmtPct, minSegment, niceCeil, rankByPrevalence, share, tip } from '../format';

const Legend = ({ gap }) => (
  <div className='flex gap-ds-5' style={{ marginBottom: gap }}>
    {[['Men', GENDER_FILL.male], ['Women', GENDER_FILL.female]].map(([label, fill]) => (
      <div key={label} className='flex items-center gap-[7px] text-[12px] text-gray-700'>
        <span className={`h-2.5 w-2.5 rounded-[3px] ${fill}`} />
        {label}
      </div>
    ))}
  </div>
);

/** One bar plus its value in a fixed mono column. `length` is drawn length, not the value — see niceCeil. */
const Bar = ({ length, label, fill, valueClassName, tooltip }) => (
  <div className='grid items-center gap-ds-3' style={{ gridTemplateColumns: '1fr 52px' }}>
    <div className={`h-[13px] rounded-ds-pill ${TRACK}`}>
      <div
        data-tip={tooltip}
        className={`h-full rounded-ds-pill ${fill}`}
        style={{ width: `${length}%`, minWidth: minSegment(length, 3) }}
      />
    </div>
    <div className={`text-right font-jetbrains-mono text-[12px] font-semibold tabular-nums ${valueClassName}`}>
      {label}
    </div>
  </div>
);

/**
 * One row per marker, each with a men's and a women's bar — the "which tests are driving this" table
 * that appears in both chapters.
 *
 * Both chapters share one axis maximum across all rows and both genders, so bar lengths stay comparable
 * down the column and across the pair. The label column is narrower in chapter 02, hence `labelWidth`.
 */
const GenderMarkerRows = ({ markers, labelWidth = 160, legendGap = 8 }) => {
  const ranked = rankByPrevalence(markers);
  const peak = ranked.reduce((max, marker) => {
    const rates = Object.values(marker.by_gender || {}).map((counts) => share(counts.affected, counts.tested));
    return Math.max(max, ...rates, 0);
  }, 0);
  const axisMax = niceCeil(peak);

  return (
    <div>
      <Legend gap={legendGap} />
      <div className='grid'>
        {ranked.map((marker, index) => (
          <div
            key={marker.key}
            className={`grid items-center gap-[18px] border-t border-gray-300 py-ds-4 ${
              index === ranked.length - 1 ? 'border-b' : ''
            }`}
            style={{ gridTemplateColumns: `${labelWidth}px 1fr` }}
          >
            <div className={`text-sm font-semibold ${marker.caveat ? 'text-gray-700' : 'text-gray-900'}`}>
              {marker.label}
              {marker.note || marker.caveat ? (
                <small
                  className={`mt-0.5 block text-[11px] font-normal ${
                    marker.caveat ? 'text-ds-amber-dk' : 'text-gray-600'
                  }`}
                >
                  {marker.caveat ? `${marker.caveat} *` : marker.note}
                </small>
              ) : null}
            </div>
            <div className='grid gap-[7px]'>
              {['male', 'female'].map((gender) => {
                const counts = (marker.by_gender || {})[gender === 'male' ? 'Male' : 'Female'] || {};
                const rate = share(counts.affected, counts.tested);
                const genderLabel = gender === 'male' ? 'Men' : 'Women';

                return (
                  <Bar
                    key={gender}
                    length={barLength(rate, axisMax)}
                    label={fmtPct(rate)}
                    fill={GENDER_FILL[gender]}
                    valueClassName={GENDER_VALUE_TEXT[gender]}
                    tooltip={tip(
                      `${marker.label} · ${genderLabel}`,
                      `${fmtPct(rate)} of ${counts.tested || 0} tested`,
                      marker.caveat
                    )}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GenderMarkerRows;
